import { collection } from '@/infra/collection'
import {
  WorkspaceMember,
  type WorkspaceMemberProps,
} from '@/features/iam/members/WorkspaceMember'
import { Id } from '@/infra/id'
import DataLoader from 'dataloader'
import { type IamApi } from '@/features/iam/api/iam-api'
import { runInAction } from 'mobx'
import {
  type MemberResource,
  type UpdateMemberRequest,
} from '@/features/iam/api/resources/members'
import { Subject } from 'rxjs'
import type {
  GetUserFn,
  PrimeWorkspaceFn,
  PrimeWorkspaceMemberProps,
  WorkspaceMemberCompositeId,
} from '@/features/iam'

/**
 * Manages workspace members.
 */
export class WorkspaceMemberStore {
  /** @see {@link profileUpdated$} */
  private readonly _profileUpdated = new Subject<WorkspaceMember>()

  /**
   * Member data loader.
   * @private
   */
  private readonly _memberLoader = new DataLoader<
    WorkspaceMemberCompositeId,
    WorkspaceMember | null,
    string
  >(this._fetchMembersCore.bind(this), {
    cacheKeyFn: (id) => `${id.workspaceId}/${id.userId}`,
  })

  /**
   * Emits when a member's profile has been updated.
   */
  readonly profileUpdated$ = this._profileUpdated.asObservable()

  /**
   * Collection of workspace members.
   */
  readonly members = collection({
    getModelId: (m) => `${m.workspaceId}/${m.userId}`,
    getDataId: (m) => `${m.workspaceId}/${m.userId}`,
    create: () => new WorkspaceMember(this._getUser),
  })

  constructor(
    private readonly _api: IamApi,
    private readonly _getUser: GetUserFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
  ) {}

  /**
   * Primes a workspace member.
   * @param props
   */
  primeWorkspaceMember(props: PrimeWorkspaceMemberProps): WorkspaceMember {
    const member = this.members.set({
      workspaceId: props.workspaceId,
      userId: props.userId,
    })

    void this.fetchWorkspaceMember(props.workspaceId, props.userId)
    return member
  }

  /**
   * Gets a workspace member by workspace and user ID.
   *
   * @param workspaceId
   * @param userId
   */
  async fetchWorkspaceMember(
    workspaceId: Id,
    userId: Id,
  ): Promise<WorkspaceMember | null> {
    return this._memberLoader.load({
      workspaceId: Id.assert(workspaceId),
      userId: Id.assert(userId),
    })
  }

  /**
   * Signs up to a workspace.
   * @param request
   */
  async signupToWorkspace(
    request: SignupToWorkspace,
  ): Promise<WorkspaceMember> {
    const props = await this._api.members
      .create({
        user_id: Id.assert(request.internalUserId),
        workspace_id: Id.assert(request.workspaceId),
        phone: request.phone,
        given_name: request.firstName,
        family_name: request.lastName,
      })
      .then(this._mapMemberResourceToProps)

    return this.members.set(props)
  }

  /**
   * Updates the member's profile.
   */
  async updateProfile(member: WorkspaceMember, update: UpdateProfile) {
    const request: UpdateMemberRequest = {
      given_name: update.givenName,
      family_name: update.familyName,
      phone: update.phone,
      email: update.email,
    }

    const response = await this._api.members.update(
      Id.assert(member.workspaceId),
      Id.assert(member.userId),
      request,
    )

    member = this.members.set(this._mapMemberResourceToProps(response))
    this._profileUpdated.next(member)
    return member
  }

  /**
   * Sends a ping to the server to indicate that the user just logged in.
   */
  async updateLastLogin(workspaceId: Id) {
    await this._api.members.updateLastLogin(Id.assert(workspaceId))
  }

  /**
   * Sends a ping to the server to indicate that the user is active.
   */
  async updateLastSeen(workspaceId: Id) {
    await this._api.members.updateLastSeen(Id.assert(workspaceId))
  }

  /**
   * Updates the avatar of the member.
   *
   * @param member
   * @param file
   * @param signal
   */
  async changeAvatar(
    member: WorkspaceMember,
    file: File,
    signal?: AbortSignal,
  ) {
    const response = await this._api.members.changeAvatar(
      Id.assert(member.workspaceId),
      Id.assert(member.userId),
      file,
      signal,
    )
    member = this.members.set(this._mapMemberResourceToProps(response))
    this._profileUpdated.next(member)
  }

  private async _fetchMembersCore(ids: readonly WorkspaceMemberCompositeId[]) {
    const resources = await this._api.members.getMany({ ids: ids.slice() })

    return runInAction(() => {
      const props = resources.map((m) =>
        m ? this._mapMemberResourceToProps(m) : null,
      )
      return this.members.set(props)
    })
  }

  private _mapMemberResourceToProps = (
    resource: MemberResource,
  ): WorkspaceMemberProps => {
    const workspaceId = Id.assert(resource.workspace_id)
    return {
      userId: Id.assert(resource.user_public_id),
      workspaceId,
      workspace: this._primeWorkspace({ id: workspaceId }),
      inactive: resource.inactive ?? false,
      approvedTermsVersion: resource.approved_terms_version ?? null,
      permissions: {
        viewJobPricing: resource.computed_permissions?.VIEW_PRICING == 'allow',
        startNewJob: resource.computed_permissions?.JOB_CREATE == 'allow',
      },
    }
  }
}

export interface SignupToWorkspace {
  internalUserId: Id
  workspaceId: Id
  firstName: string
  lastName: string
  phone: string
}

export interface UpdateProfile {
  givenName: string
  familyName: string
  email?: string
  phone: string
}
